import { Button, Dropdown, Empty, MenuProps } from 'antd';
import Table, { ColumnsType, TablePaginationConfig, TableProps } from 'antd/es/table';
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/es/table/interface';

export type ActionMenuProps<RecordType extends object> = Omit<MenuProps, 'onClick'> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (info: any, record: RecordType) => void;
};

export type SelectTableDisabledType<RecordType> =
  | boolean
  | {
      remove?: boolean | ((record: RecordType) => boolean);
      select?: boolean | ((record: RecordType) => boolean);
    };

export type SelectTableProps<RecordType extends ValueType, ValueType extends object> = Omit<
  TableProps<RecordType>,
  'onChange'
> & {
  actions?: ActionMenuProps<RecordType>;
  mode?: 'single' | 'multiple';
  value?: ValueType[];
  disabled?: SelectTableDisabledType<RecordType>;
  onChange?: (newValue: ValueType[]) => void;
  onTableChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<RecordType> | SorterResult<RecordType>[],
    extra: TableCurrentDataSource<RecordType>,
  ) => void;
};

export const SelectTable = <T extends U, U extends object>({
  actions,
  columns: _columns = [],
  disabled,
  mode = 'multiple',
  rowKey = 'id',
  value = [],
  onChange,
  onTableChange,
  ...tableProps
}: SelectTableProps<T, U>) => {
  const equals = (a: T, b: U): boolean => {
    if (typeof rowKey === 'function') {
      return rowKey(a) === rowKey(b as T);
    } else {
      return (a[rowKey as keyof T] as React.Key) === (b[rowKey as keyof U] as React.Key);
    }
  };

  const handleAdd = (record: T) => {
    const newList = mode === 'multiple' ? value.concat(record) : [record];
    onChange && onChange(newList);
  };

  const handleRemove = (record: T) => {
    const newList = value?.filter((x) => !equals(record, x));
    onChange && onChange(newList ?? []);
  };

  const selectText = mode === 'multiple' ? 'Add' : 'Select';

  const columns: ColumnsType<T> = [
    {
      title: 'Action',
      width: 125,
      render: (_, record) => {
        const isSelected = value?.find((x) => equals(record, x));
        const isDisabled =
          typeof disabled === 'object'
            ? isSelected
              ? typeof disabled.remove === 'function'
                ? disabled.remove(record)
                : disabled.remove
              : typeof disabled.select === 'function'
                ? disabled.select(record)
                : disabled.select
            : disabled;
        const handleOnClick = isSelected ? () => handleRemove(record) : () => handleAdd(record);
        const buttonText = isSelected ? 'Remove' : selectText;

        if (actions) {
          return (
            <Dropdown.Button
              disabled={isDisabled}
              menu={{ items: actions.items, onClick: (e) => actions.onClick && actions.onClick(e, record) }}
              size="small"
              onClick={handleOnClick}
            >
              {buttonText}
            </Dropdown.Button>
          );
        }

        return (
          <Button
            disabled={isDisabled}
            ghost={!!isSelected}
            size="small"
            type={isSelected ? 'primary' : undefined}
            onClick={handleOnClick}
          >
            {buttonText}
          </Button>
        );
      },
    },
    ..._columns,
  ];

  if (!tableProps.loading && !tableProps.dataSource) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return <Table columns={columns} rowKey={rowKey} onChange={onTableChange} {...tableProps} />;
};
